import React, { useEffect, useState } from "react";
import icon_password_hide from "../../Assets/svg/icon_password_hide.svg";
import icon_password_view from "../../Assets/svg/icon_password_view.svg";

import isEmpty from "../../App/validation/is-empty";
import dict from "../../Data/dict.json";

import { get_country_calling_codes } from "../../App/validation/countries";
import { MenuItem, Select } from "@mui/material";
import { get_country_code_and_phone_number } from "../../App/validation/phone_number";

const phoneNumberList = get_country_calling_codes();

/**
 * MIDDLEWARE: Safety translation helper
 * Prevents "undefined" errors if a key is missing from JSON
 */
const translate = (key, language) => {
  if (!key) return "";
  // If key exists in dict and has the language, use it.
  // Otherwise, return the key itself (the raw API error).
  return dict?.[key]?.[language] || key;
};

const styleObjectNormal = {
  overflow: "hidden",
  boxSizing: "border-box",
  borderRadius: "7px",
  border: "2px solid #e8ecf4",
  backgroundColor: "#ffffff",
  paddingLeft: "10px",
  fontSize: "16px",
  color: "#000000",
  textAlign: "left",
  width: "100%",
  height: "60px",
};

const styleObjectError = {
  overflow: "hidden",
  boxSizing: "border-box",
  borderRadius: "7px",
  border: "2px solid red",
  backgroundColor: "#ffffff",
  padding: "10px 10px 10px 10px",
  fontSize: "16px",
  color: "#000000",
  textAlign: "left",
  width: "100%",
  height: "60px",
};

const styleObjectPassword = {
  overflow: "hidden",
  boxSizing: "border-box",
  borderRadius: "7px",
  marginBottom: "0px",
  border: "none",
  backgroundColor: "#ffffff",
  paddingLeft: "5px",
  fontSize: "16px",
  color: "#000000",
  textAlign: "left",
  width: "100%",
  height: "60px",
};

const stylePasswordNormal = {
  borderRadius: "7px",
  border: "2px solid #e8ecf4",
  backgroundColor: "#ffffff",
};

const stylePasswordError = {
  borderRadius: "7px",
  border: "2px solid red",
  backgroundColor: "#ffffff",
};

const TextFieldGroup = (props) => {
  const {
    name,
    placeholder,
    value,
    error,
    info,
    type,
    onChange,
    code,
    onCodeChange,
    disabled,
    list,
    id,
    toggleShowPassword,
    isPassword,
    list_array,
    pattern,
    defaultValue,
    Title,
    handleKeyDown,
    country,
    ...rest
  } = props;

  const [countryCode, setCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const language = localStorage?.language ? localStorage?.language : "ina";

  // USE TRANSLATION MIDDLEWARE
  let title = Title ? Title : placeholder;
  if (error) {
    title = translate(error, language);
  }

  const onChangeCountryCode = (newCode) => {
    if (onCodeChange) {
      onCodeChange(newCode);
    } else {
      const e = {
        target: {
          name: "phone_number",
          value: newCode + phoneNumber,
        },
      };
      setCountryCode(newCode);
      onChange(e);
    }
  };

  const onChangePhone = (inputValue) => {
    let sanitizedNumber = inputValue.replace(/\D/g, "");
    if (sanitizedNumber.startsWith("0")) {
      sanitizedNumber = sanitizedNumber.substring(1);
    }

    if (onCodeChange) {
      const e = {
        target: {
          name: name,
          value: sanitizedNumber,
        },
      };
      setPhoneNumber(sanitizedNumber);
      onChange(e);
    } else {
      const finalValue = countryCode + sanitizedNumber;
      const e = {
        target: {
          name: "phone_number",
          value: finalValue,
        },
      };
      setPhoneNumber(sanitizedNumber);
      onChange(e);
    }
  };

  useEffect(() => {
    if (type === "phone_number") {
      const { countryCode: extractedCode, phoneNumber: extractedPhone = "" } =
        get_country_code_and_phone_number(value);

      const initial_country_code =
        phoneNumberList.find((item) => item?.country_name === country)
          ?.calling_code || "62";
      setCountryCode(extractedCode || initial_country_code);
      setPhoneNumber(extractedPhone);
    }
  }, [country, type, value]);

  const commonTitleStyle = {
    textAlign: "left",
    color: error ? "red" : "black",
    fontSize: "1rem",
    fontWeight: "600",
    margin: "10px 0 10px 0",
  };

  let itemContent;
  if (isPassword) {
    itemContent = (
      <div className="input_old">
        <div style={commonTitleStyle}>{title}</div>
        <div
          className="input_icon"
          style={error ? stylePasswordError : stylePasswordNormal}
        >
          <input
            type={type}
            placeholder={placeholder}
            name={name}
            id={id}
            value={value}
            onChange={onChange}
            disabled={disabled}
            style={styleObjectPassword}
            autoComplete="off"
            onKeyDown={(e) => handleKeyDown && handleKeyDown(e)}
            {...rest}
          />
          <img
            className="pointer"
            alt="show password"
            src={type === "password" ? icon_password_view : icon_password_hide}
            width="25px"
            onClick={toggleShowPassword}
          />
        </div>
        {info && <small>{info}</small>}
      </div>
    );
  } else {
    if (isEmpty(list_array)) {
      if (type === "phone_number") {
        itemContent = (
          <div className="input_old">
            <div style={commonTitleStyle}>{title}</div>
            <div className="flex gap_5">
              <Select
                value={code || countryCode}
                onChange={(e) => onChangeCountryCode(e.target.value)}
                renderValue={() => `+${code || countryCode}`}
              >
                {phoneNumberList.map(
                  ({ country_name, calling_code }, index) => (
                    <MenuItem key={index} value={calling_code}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>{calling_code}</span>
                        <span style={{ color: "#888" }}>{country_name}</span>
                      </div>
                    </MenuItem>
                  ),
                )}
              </Select>
              <input
                type="text"
                inputMode="numeric"
                placeholder={placeholder}
                name={name}
                id={id}
                value={phoneNumber}
                onChange={(e) => onChangePhone(e.target.value)}
                disabled={disabled}
                style={error ? styleObjectError : styleObjectNormal}
                autoComplete="off"
                onKeyDown={(e) => handleKeyDown && handleKeyDown(e)}
                {...rest}
              />
            </div>
            {info && <small>{info}</small>}
            <label style={{ display: "block", marginTop: "5px" }}>
              {translate("Insert country code", language)}
            </label>
          </div>
        );
      } else {
        itemContent = (
          <div className="input_old">
            <div style={commonTitleStyle}>{title}</div>
            <input
              type={type}
              placeholder={placeholder}
              name={name}
              id={id}
              value={value}
              onChange={onChange}
              disabled={disabled}
              style={error ? styleObjectError : styleObjectNormal}
              autoComplete="off"
              onKeyDown={(e) => handleKeyDown && handleKeyDown(e)}
              {...rest}
            />
            {info && <small>{info}</small>}
          </div>
        );
      }
    } else {
      itemContent = (
        <div className="input_old">
          <div style={commonTitleStyle}>{title}</div>
          <input
            type={type}
            placeholder={placeholder}
            name={name}
            id={id}
            value={value}
            onChange={onChange}
            disabled={disabled}
            style={error ? styleObjectError : styleObjectNormal}
            list={list}
            autoComplete="off"
            onKeyDown={(e) => handleKeyDown && handleKeyDown(e)}
            {...rest}
          />
          <datalist id={list}>
            {list_array.map((element, idx) => (
              <option value={element} key={idx} />
            ))}
          </datalist>
          {info && <small>{info}</small>}
        </div>
      );
    }
  }
  return itemContent;
};

TextFieldGroup.defaultProps = {
  type: "text",
};

export const SelectField = ({
  name,
  placeholder,
  value,
  error,
  info,
  onChange,
  disabled,
  id,
  list_array,
}) => {
  const language = localStorage?.language ? localStorage?.language : "ina";

  // USE TRANSLATION MIDDLEWARE
  let title = placeholder;
  if (error) {
    title = translate(error, language);
  }

  return (
    <div className="input_old">
      <div
        style={{
          textAlign: "left",
          color: error ? "red" : "black",
          fontSize: "1rem",
          fontWeight: "600",
          margin: "10px 0 10px 0",
        }}
      >
        {title}
      </div>
      <div className="select-wrapper">
        <select
          name={name}
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          style={error ? styleObjectError : styleObjectNormal}
          className="remove-select-arrow"
        >
          <option value="" hidden>
            {translate("Choose", language)}
          </option>
          {list_array.map((item, index) => (
            <option value={item.value} key={index}>
              {item.title}
            </option>
          ))}
        </select>
      </div>
      {info && <small>{info}</small>}
    </div>
  );
};

export default TextFieldGroup;
